import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useCallback, useRef, useState } from 'react';
import { HeaderConfig } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import { PaginatedResponse } from '@interfaces/machines';
import { OperationSearchItem, PartSearchItem } from '@interfaces/search';
import { useSelector } from 'react-redux';
import {
  selectForceUpdateHeaderConfig,
  setJobId,
  setJobs,
  setNcFileId,
  setNcFiles,
  setParts,
  setPartId,
  setNcFileData,
} from '../../../../../reducers/details';
import { GetBatchesByEntityId, GetNCFilesByEntityId, LoadSingleBatch, SearchEntity } from '@interfaces/methods';
import { parseRouteParamAsNumber } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { setBatchId } from '@reducers/batch';

interface UsePartDetailsProps {
  getPartByID: (partId: number) => Promise<PaginatedResponse<PartSearchItem>>;
  getProductionBatchByPart: GetBatchesByEntityId;
  getNCFileByPartId: GetNCFilesByEntityId;
  getTools?: (param: { ncFileId: number }) => Promise<void>;
  clearTools?: () => void;
  updateHeaderConfig?: (headerConfig: HeaderConfig) => void;
  operationsSearch?: SearchEntity<OperationSearchItem>;
  getAllNcFiles?: boolean;
  getAllJobs?: boolean;
  loadSingleBatch?: LoadSingleBatch;
}

export const usePartDetails = ({
  getProductionBatchByPart,
  getNCFileByPartId,
  getTools,
  clearTools,
  updateHeaderConfig,
  getPartByID,
  operationsSearch,
  getAllNcFiles,
  getAllJobs,
  loadSingleBatch,
}: UsePartDetailsProps) => {
  const route = useRoute();
  const dispatch = useAppDispatch();

  const forceUpdateHeaderConfig = useSelector(selectForceUpdateHeaderConfig);

  const [ncFileDataLoading, setNcFileDataLoading] = useState<boolean>(true);
  const [partDataLoading, setPartDataLoading] = useState<boolean>(true);
  const [jobDataLoading, setJobDataLoading] = useState<boolean>(true);

  const ncFileIdRef = useRef<number | null>(null);

  const partId = parseRouteParamAsNumber(route.params?.partId);

  const loadAndSetJobs = async () => {
    if (partId == null || !operationsSearch) {
      setJobDataLoading(false);
      return;
    }

    const jobs = await operationsSearch({
      data: { ...(!getAllJobs ? { top: 1 } : {}) },
      filter: { oDataFilter: `PartId eq ${partId}`, orderBy: ['ModifiedOn desc'] },
      select: null,
      resetHistory: true,
      ignoreHistory: true,
    });
    if (jobs?.searchResult) {
      dispatch(setJobs(jobs.searchResult));
      if (jobs.searchResult?.length === 1) {
        dispatch(setJobId(jobs.searchResult?.[0]?.productionOperationId));
      }
    }
    setJobDataLoading(false);
    return jobs;
  };

  const loadBatches = async () => {
    const batches = getProductionBatchByPart
      ? await getProductionBatchByPart({ id: partId, filters: { top: 1 }, select: ['ProductionBatchId', 'BatchNumber'] })
      : undefined;
    if (batches?.totalResultCount === 1) {
      const loadedBatchId = batches.searchResult?.[0]?.productionBatchId;
      dispatch(setBatchId(loadedBatchId || null));
      await loadSingleBatch?.(loadedBatchId);
    }
    return batches;
  };

  const loadAndSetPartDetails = async () => {
    if (!getPartByID) {
      setPartDataLoading(false);
      return;
    }

    const partDetails = await getPartByID(partId);
    if (partDetails?.searchResult?.length) {
      dispatch(setParts([partDetails.searchResult[0]]));
      dispatch(setPartId(partDetails.searchResult?.[0]?.partId));
    }
    setPartDataLoading(false);
    return partDetails;
  };

  const loadAndSetNCFiles = async () => {
    if (!getNCFileByPartId) {
      setNcFileDataLoading(false);
    }
    const ncFilesData = await getNCFileByPartId(partId, !getAllNcFiles ? { top: 1 } : null);
    if (ncFilesData?.searchResult) {
      if (ncFilesData?.searchResult?.length === 1) {
        const ncFileId = ncFilesData?.searchResult?.[0]?.ncFileId;
        ncFileIdRef.current = ncFileId;
        dispatch(setNcFileId(ncFileId));
        dispatch(setNcFileData(ncFilesData?.searchResult?.[0]));
      }
      dispatch(setNcFiles(ncFilesData?.searchResult));
    }

    setNcFileDataLoading(false);
    return ncFilesData;
  };

  const loadDataAndDefineHeader = async () => {
    const partDetails = await loadAndSetPartDetails();
    const batchesData = await loadBatches();
    const ncFilesData = await loadAndSetNCFiles();
    const jobsData = await loadAndSetJobs();

    // if (getTools) {
    //   await getTools({ ncFileId });
    // } else if (clearTools) {
    //   await clearTools();
    // }

    const jobHeader =
      jobsData?.totalResultCount === 1 ? `${jobsData.searchResult?.[0]?.productionOperationNumber}` : `#${jobsData?.totalResultCount ?? 0}`;

    const batchHeader = batchesData?.totalResultCount === 1 ? batchesData?.searchResult?.[0]?.batchNumber : `#${batchesData?.totalResultCount ?? 0}`;

    if (updateHeaderConfig) {
      updateHeaderConfig({
        job: jobHeader,
        batch: batchHeader,
        ncFile: ncFilesData?.totalResultCount === 1 ? ncFilesData.searchResult[0].ncFileName : `#${ncFilesData?.totalResultCount ?? '0'}`,
        ncFileId: ncFilesData?.searchResult[0]?.ncFileId ?? '',
        ncFileStatus: ncFilesData?.searchResult?.[0]?.ncFileStatus ?? '',
        part: {
          id: parseRouteParamAsNumber(route.params?.partId) ?? partDetails?.searchResult[0]?.partId ?? '',
          name: route.params?.partName ?? partDetails?.searchResult[0]?.partName ?? '',
        },
        productionOrder: '',
        tools: '#0',
      });
    }
  };

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      const fetchData = async () => {
        if (isActive) {
          await loadDataAndDefineHeader();
        }
      };

      fetchData();

      return () => {
        isActive = false;
      };
    }, [partId, forceUpdateHeaderConfig])
  );

  const refetchTools = useCallback(() => {
    if (ncFileIdRef.current && getTools) {
      getTools({ ncFileId: ncFileIdRef.current });
    }
  }, [getTools]);

  return { ncFileDataLoading, partDataLoading, jobDataLoading, refetchTools };
};
